import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Search,
  Upload,
  Plus,
  FileText,
  Building2,
  Users,
  LayoutDashboard,
  Tag,
  Clock,
  ArrowRight,
  ClipboardList,
  HardDrive,
  MessageSquare,
  BookOpen,
  ShieldCheck,
  Route as RouteIcon,
  BookText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getContractFacets, searchContracts } from '../features/contracts/contractLookupApi';
import { searchRequests } from '../features/requests/requestLookupApi';
import { searchReviews } from '../features/reviews/reviewLookupApi';
import { searchIntakes } from '../features/generate/intakeLookupApi';
import { RequestStatusBadge, RiskRatingBadge } from './RequestBadges';
import { ReviewStatusChip, TriageChip } from '../features/reviews/reviewBits';
import { intakeTypeLabel, IntakeStatusChip } from '../features/generate/intakeBits';
import { counterpartyPathForName } from '../features/counterparties/counterpartyApi';
import { isTopDialog, popDialog, pushDialog, trapTabWithin } from '../utils/focus';

/**
 * Global command palette. Keyboard-first navigation + search across every
 * addressable surface in the app:
 *   - Actions (upload, new TPRM request, admin, etc.) gated by role
 *   - Navigation (dashboard, contracts, requests, drive settings)
 *   - Live contract search  (GET /api/contracts/search?q=…)
 *   - Live TPRM request search  (GET /api/requests?q=…, backend-scoped to the
 *     caller's own requests unless they have elevated access)
 *   - Live document-review (NDA) search  (GET /api/reviews?q=…, backend-scoped
 *     to the caller's own reviews unless they are a reviewer)
 *   - Live intake/screening-session search  (GET /api/generate/sessions?q=…,
 *     backend-scoped to the caller's own intakes unless they are legal/admin)
 *   - Counterparties / groups / tags (from facets, for jump-to-filtered-list)
 *   - Recent contracts the user has viewed (localStorage)
 *
 * Triggered by ⌘K / Ctrl+K from anywhere. Ignores the shortcut when a
 * modifier-less key is being typed into a form (the ⌘/Ctrl already prevents
 * that, but Safari sometimes dispatches the chord even for contenteditable).
 *
 * Also owns the `/` accelerator: on a page that ships a search box (the
 * `[data-search-input]` hook on the list pages) `/` focuses it, and on every
 * other page `/` opens this palette instead, so the key is never dead.
 */

const RECENT_KEY = 'commandk.recentContracts';
const MAX_RECENT = 8;

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * True when the element already owns text entry, so a bare `/` must be typed
 * rather than treated as a shortcut. Without this, a slash could never be
 * typed into a search box or a contract notes field.
 */
function ownsTextEntry(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  return TYPING_TAGS.has(el.tagName);
}

/** Public helper the app can call to record that a contract was visited. */
export function trackRecentContract(contract) {
  if (!contract || !contract.id) return;
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const existing = raw ? JSON.parse(raw) : [];
    const without = existing.filter((c) => c.id !== contract.id);
    const entry = {
      id: contract.id,
      title: contract.title || '',
      shortName: contract.shortName || '',
      counterparty: contract.counterparty || '',
      contractType: contract.contractType || '',
      visitedAt: Date.now(),
    };
    const next = [entry, ...without].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* localStorage may be disabled */ }
}

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

export default function CommandK() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can, canWriteContracts, isAdmin } = useAuth();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Server-backed results for typed queries. `cmdk` handles the local filter
  // over Action/Navigation items, but contract search needs the backend's
  // LIKE-over-title/counterparty query to stay fast for 900+ contracts.
  const [contractResults, setContractResults] = useState([]);
  const [requestResults, setRequestResults] = useState([]);
  const [reviewResults, setReviewResults] = useState([]);
  const [intakeResults, setIntakeResults] = useState([]);
  const [facets, setFacets] = useState({ counterparties: [], counterpartyGroups: [], tags: [] });
  const [recent, setRecent] = useState([]);

  // Read inside the always-mounted keydown listener below, which is
  // registered once — a ref keeps it from closing over a stale `open`.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  // The overlay/dialog element, scoping the focus-trap's focusable query.
  const dialogRef = useRef(null);

  // Whatever had focus when the palette opened, so closing can hand it back.
  // Captured at the moment we open rather than inside the open-gated effect
  // below: React applies the input's autoFocus during commit, before effects
  // run, so by then document.activeElement is already the palette's own input.
  const previouslyFocusedRef = useRef(null);
  const openPalette = useCallback(() => {
    previouslyFocusedRef.current = document.activeElement;
    setOpen(true);
  }, []);

  // Global ⌘K / Ctrl+K trigger and the `/` search accelerator, plus an
  // imperative open via custom event so the Topbar search pill can lift the
  // palette without a ref.
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        if (openRef.current) setOpen(false);
        else openPalette();
        return;
      }
      // Bare `/` (a shifted slash is `?` on most layouts, so no shiftKey
      // check is needed — e.key already differs).
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      // A dialog owns the keyboard while it is up: the palette itself (where
      // focus may sit on a result row rather than its input) and any Modal.
      // Both are checked — the state flag is the contract, the DOM query
      // catches modals this component knows nothing about.
      if (openRef.current) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (ownsTextEntry(document.activeElement)) return;
      const pageSearch = document.querySelector('[data-search-input]');
      // preventDefault either way, or the slash lands in the box we focus.
      e.preventDefault();
      if (pageSearch) pageSearch.focus();
      else openPalette();
    };
    const onOpen = () => openPalette();
    document.addEventListener('keydown', onKey);
    document.addEventListener('commandk:open', onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('commandk:open', onOpen);
    };
  }, [openPalette]);

  // Focus containment while the palette is up. The wrapper carries
  // role=dialog aria-modal=true, which promises assistive tech that focus is
  // confined — so Tab must not walk out into the app chrome behind the
  // overlay, and Escape has to work from wherever focus ended up. Both live on
  // a document listener for that second reason: a React onKeyDown on the
  // wrapper only sees keydowns that bubble out of the palette, so once focus
  // escaped, Escape was dead and the footer's "esc to close" hint was a lie.
  // Same algorithm as Modal.js, through the shared helper. Deliberately NOT
  // folded into the always-mounted ⌘K listener above — that one stays
  // registered once and must not query the DOM on every keystroke.
  useEffect(() => {
    if (!open) return undefined;
    // Local, not dialogRef.current, because React detaches the ref before this
    // effect's cleanup runs — see the same note in Modal.js.
    const dialogNode = dialogRef.current;
    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        // Escape belongs to the topmost dialog only. ⌘K opens the palette over
        // an open Modal, and Modal listens on document too, so without this one
        // Escape closed both (doc-262).
        if (!isTopDialog(dialogNode)) return;
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      trapTabWithin(e, dialogRef.current);
    };
    pushDialog(dialogNode);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      popDialog(dialogNode);
      document.removeEventListener('keydown', handleKeydown);
      // Hand focus back to whatever opened the palette — a trap that dumps
      // focus on <body> when it lets go is only half a trap. Unlike Modal,
      // one of the close paths is go() -> navigate(), which unmounts the page
      // the trigger lived on, hence the contains() guard.
      const previous = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [open]);

  // Reload recent + facets whenever the palette opens — cheap, keeps the list
  // current.
  useEffect(() => {
    if (!open) return;
    setRecent(loadRecent());
    getContractFacets()
      .then((data) => { if (data) setFacets(data); })
      .catch(() => {});
  }, [open]);

  // Reset query/results when opening; close on navigation.
  useEffect(() => { if (!open) { setQuery(''); setContractResults([]); setRequestResults([]); setReviewResults([]); setIntakeResults([]); } }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Debounced server search — contracts, TPRM requests, document reviews and
  // intakes in parallel, one shared timer so all result groups land together.
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) { setContractResults([]); setRequestResults([]); setReviewResults([]); setIntakeResults([]); return; }
    const t = setTimeout(() => {
      searchContracts({ q, limit: 10 })
        .then((data) => setContractResults(data?.contracts || []))
        .catch(() => setContractResults([]));
      searchRequests({ q, limit: 8 })
        .then((data) => setRequestResults(data?.requests || []))
        .catch(() => setRequestResults([]));
      searchReviews({ q, limit: 8 })
        .then((data) => setReviewResults(data?.reviews || []))
        .catch(() => setReviewResults([]));
      searchIntakes({ q, limit: 8 })
        .then((data) => setIntakeResults(data?.sessions || []))
        .catch(() => setIntakeResults([]));
    }, 180);
    return () => clearTimeout(t);
  }, [open, query]);

  const go = useCallback((to) => {
    setOpen(false);
    navigate(to);
  }, [navigate]);

  // Scoped prefixes let power users pin to a section. `>` actions, `@`
  // counterparties, `#` tags, `/` groups. Anything else searches everything.
  const scope = useMemo(() => {
    const q = query;
    if (q.startsWith('>')) return { kind: 'actions', term: q.slice(1).trim() };
    if (q.startsWith('@')) return { kind: 'counterparties', term: q.slice(1).trim() };
    if (q.startsWith('#')) return { kind: 'tags', term: q.slice(1).trim() };
    if (q.startsWith('/')) return { kind: 'groups', term: q.slice(1).trim() };
    return { kind: 'all', term: q.trim() };
  }, [query]);

  // Build the Actions list up-front so we can filter it (cmdk's built-in
  // filter is off — see shouldFilter={false}).
  const actionItems = useMemo(() => {
    const all = [];
    if (can('upload_contracts')) all.push({ icon: Upload, label: 'Upload contract', hint: 'Scan a new PDF or DOCX', go: '/contracts/upload' });
    if (canWriteContracts) all.push({ icon: Plus, label: 'New contract', hint: 'Create from scratch', go: '/contracts/new' });
    all.push({ icon: Plus, label: 'New TPRM request', hint: 'Submit a vendor onboarding request', go: '/tprm/new' });
    // Same capability gates as the sidebar links in App.js — ⌘K must not
    // show more or less than the nav does.
    if (can('view_conversations')) all.push({ icon: MessageSquare, label: 'Assistant conversations', hint: 'Review bot transcripts', go: '/admin/conversations' });
    if (isAdmin) all.push({ icon: Users, label: 'Manage users', go: '/admin/users' });
    if (canWriteContracts) all.push({ icon: BookOpen, label: 'Review playbooks', hint: 'Edit NDA + TPRM review criteria', go: '/admin/playbooks' });
    if (can('manage_settings')) all.push({ icon: HardDrive, label: 'Admin settings', go: '/admin/settings' });
    return all;
  }, [can, canWriteContracts, isAdmin]);

  const navItems = useMemo(() => ([
    { icon: LayoutDashboard, label: 'Dashboard', go: '/' },
    { icon: FileText, label: 'All contracts', go: '/contracts' },
    { icon: FileText, label: 'TPRM requests', go: '/tprm' },
    { icon: ShieldCheck, label: 'Document reviews', go: '/reviews' },
    { icon: BookText, label: 'Documentation', go: '/docs' },
  ]), []);

  // Visibility + filtering per scope. When a prefix is active, only the
  // matching section is shown; within that section we substring-match the
  // term against the item's label + hint.
  const showActions = scope.kind === 'all' || scope.kind === 'actions';
  const showNav = scope.kind === 'all';
  const showCounterparties = scope.kind === 'all' || scope.kind === 'counterparties';
  const showGroups = scope.kind === 'all' || scope.kind === 'groups';
  const showTags = scope.kind === 'all' || scope.kind === 'tags';
  const showRecent = scope.kind === 'all' && !scope.term;
  const showContracts = scope.kind === 'all' && scope.term.length >= 2;
  const showRequests = scope.kind === 'all' && scope.term.length >= 2;
  const showReviews = scope.kind === 'all' && scope.term.length >= 2;
  const showIntakes = scope.kind === 'all' && scope.term.length >= 2;

  const visibleActions = showActions
    ? filterItems(actionItems, scope.term, (x) => `${x.label} ${x.hint || ''}`)
    : [];
  const visibleNav = showNav
    ? filterItems(navItems, scope.term, (x) => x.label)
    : [];
  const visibleCounterparties = showCounterparties
    ? filterItems(facets.counterparties || [], scope.term, (x) => x).slice(0, 8)
    : [];
  const visibleGroups = showGroups
    ? filterItems(facets.counterpartyGroups || [], scope.term, (x) => x.name).slice(0, 8)
    : [];
  const visibleTags = showTags
    ? filterItems(facets.tags || [], scope.term, (x) => x).slice(0, 8)
    : [];

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      // Focusable only as the trap's last-resort target if the panel ever
      // renders with no tab stop at all; -1 keeps it out of the tab order.
      tabIndex={-1}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      // Escape (and Tab) are handled by the document listener in the effect
      // above, not here: this is a hand-rolled dialog, not cmdk's
      // Command.Dialog, and cmdk itself never handles Escape (that comes from
      // the Radix dialog Command.Dialog wraps). A handler on this wrapper only
      // fires for keydowns bubbling out of the palette, which is exactly the
      // hole doc-258 closed.
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 100,
      }}
    >
      <Command
        label="Command menu"
        // We filter entries ourselves so scope prefixes (>, @, #, /) and
        // server-side contract search can coexist. Leaving cmdk's built-in
        // filter on would mismatch because the input value includes the
        // literal prefix character.
        shouldFilter={false}
        style={{
          width: 640,
          maxWidth: 'calc(100vw - 48px)',
          background: 'var(--surface-panel)',
          border: 'var(--stroke-xs) solid var(--border-primary)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-lg, 0 20px 60px rgba(0,0,0,0.3))',
          overflow: 'hidden',
          fontFamily: 'inherit',
        }}
      >
        <div
          className="row"
          style={{
            padding: '12px 14px',
            borderBottom: 'var(--stroke-xs) solid var(--border-primary)',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <Search className="w-4 h-4" style={{ color: 'var(--icon-tertiary)' }} />
          <Command.Input
            // The whole subtree unmounts when the palette closes (see the
            // `if (!open) return null` above), so it mounts fresh on every
            // open and React honours autoFocus — without it the palette opens
            // with focus on <body> and swallows everything you type.
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search contracts, TPRM requests, reviews, intakes…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 14,
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              minWidth: 0,
            }}
          />
          <span className="muted mono" style={{ fontSize: 11 }}>Esc to close</span>
        </div>

        <Command.List
          style={{
            maxHeight: 420,
            overflowY: 'auto',
            padding: 6,
          }}
        >
          <Command.Empty style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            No matches. Try a different term.
          </Command.Empty>

          {/* ───── Actions ───── */}
          {showActions && visibleActions.length > 0 && (
            <Command.Group heading="Actions">
              {visibleActions.map((a) => (
                <CmdItem
                  key={a.label}
                  icon={a.icon}
                  label={a.label}
                  hint={a.hint}
                  onSelect={() => go(a.go)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Navigation ───── */}
          {showNav && visibleNav.length > 0 && (
            <Command.Group heading="Navigation">
              {visibleNav.map((n) => (
                <CmdItem key={n.label} icon={n.icon} label={n.label} onSelect={() => go(n.go)} />
              ))}
            </Command.Group>
          )}

          {/* ───── Contract search results ───── */}
          {showContracts && contractResults.length > 0 && (
            <Command.Group heading={`Contracts — ${contractResults.length} match${contractResults.length === 1 ? '' : 'es'}`}>
              {contractResults.map((c) => (
                <CmdItem
                  key={c.id}
                  value={`contract-${c.id}`}
                  icon={FileText}
                  label={c.title}
                  // Content hits (doc-101) show WHERE the term matched instead
                  // of metadata that plainly doesn't contain it. Snippets are
                  // plain text with «guillemet» markers — never render as HTML.
                  hint={c.matchedIn === 'document' && c.snippet
                    ? `matched in document: ${c.snippet}`
                    : [c.shortName, c.counterparty, c.contractType].filter(Boolean).join(' · ')}
                  onSelect={() => go(`/contracts/${c.id}`)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── TPRM request search results ───── */}
          {showRequests && requestResults.length > 0 && (
            <Command.Group heading={`TPRM requests — ${requestResults.length} match${requestResults.length === 1 ? '' : 'es'}`}>
              {requestResults.map((r) => (
                <CmdItem
                  key={r.id}
                  value={`tprm-request-${r.id}`}
                  icon={ClipboardList}
                  label={r.vendorName || '(unnamed vendor)'}
                  hint={[r.serviceOwner, r.requesterName].filter(Boolean).join(' · ')}
                  badge={(
                    <>
                      <RequestStatusBadge status={r.status} size="small" />
                      {(r.recommendedRiskRating || r.riskRating) && (
                        <RiskRatingBadge rating={r.recommendedRiskRating || r.riskRating} size="small" />
                      )}
                    </>
                  )}
                  onSelect={() => go(`/tprm/${r.id}`)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Document review (NDA) search results ───── */}
          {showReviews && reviewResults.length > 0 && (
            <Command.Group heading={`Document reviews — ${reviewResults.length} match${reviewResults.length === 1 ? '' : 'es'}`}>
              {reviewResults.map((r) => (
                <CmdItem
                  key={r.id}
                  value={`document-review-${r.id}`}
                  icon={ShieldCheck}
                  label={r.title || r.filename || '(untitled review)'}
                  hint={[
                    r.requesterName,
                    r.filename && r.filename !== r.title ? r.filename : null,
                  ].filter(Boolean).join(' · ')}
                  badge={(
                    <>
                      <ReviewStatusChip status={r.status} />
                      {r.aiStatus === 'complete' && r.aiClassification && (
                        <TriageChip aiStatus={r.aiStatus} classification={r.aiClassification} />
                      )}
                    </>
                  )}
                  onSelect={() => go(`/reviews/${r.id}`)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Intake / screening session search results ───── */}
          {showIntakes && intakeResults.length > 0 && (
            <Command.Group heading={`Intakes — ${intakeResults.length} match${intakeResults.length === 1 ? '' : 'es'}`}>
              {intakeResults.map((s) => (
                <CmdItem
                  key={s.id}
                  value={`intake-${s.id}`}
                  icon={RouteIcon}
                  label={s.prospectName || 'Untitled intake'}
                  // Whose intake it is only matters when it isn't the caller's
                  // (legal/admin search everyone's).
                  hint={[
                    intakeTypeLabel(s.typeKey),
                    s.isOwn ? null : (s.ownerName || s.ownerEmail),
                  ].filter(Boolean).join(' · ')}
                  badge={<IntakeStatusChip status={s.status} />}
                  onSelect={() => go(`/intake/sessions/${s.id}`)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Counterparties ───── */}
          {/* Picking a counterparty opens its 360 page (doc-174), not a
              filtered contracts list: the whole relationship — contracts, TPRM
              requests, upcoming dates, activity — is what the name is being
              searched for. The facets only carry the raw string, so both groups
              link through the name-resolving key form and let the backend fold
              spellings/group members with its own normalizer. */}
          {showCounterparties && visibleCounterparties.length > 0 && (
            <Command.Group heading="Counterparties">
              {visibleCounterparties.map((name) => (
                <CmdItem
                  key={name}
                  icon={Building2}
                  label={name}
                  hint="Open the counterparty page"
                  onSelect={() => go(counterpartyPathForName(name))}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Groups ───── */}
          {showGroups && visibleGroups.length > 0 && (
            <Command.Group heading="Counterparty groups">
              {visibleGroups.map((g) => (
                <CmdItem
                  key={g.name}
                  icon={Building2}
                  label={g.name}
                  hint={`${g.count} contract${g.count === 1 ? '' : 's'}`}
                  onSelect={() => go(counterpartyPathForName(g.name))}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Tags (initiatives) ───── */}
          {showTags && visibleTags.length > 0 && (
            <Command.Group heading="Initiatives / tags">
              {visibleTags.map((t) => (
                <CmdItem
                  key={t}
                  icon={Tag}
                  label={t}
                  hint="Filter the contracts list"
                  onSelect={() => go(`/contracts?tag=${encodeURIComponent(t)}`)}
                />
              ))}
            </Command.Group>
          )}

          {/* ───── Recent ───── */}
          {showRecent && recent.length > 0 && (
            <Command.Group heading="Recently viewed">
              {recent.map((c) => (
                <CmdItem
                  key={c.id}
                  icon={Clock}
                  label={c.title || '(untitled)'}
                  hint={[c.shortName, c.counterparty].filter(Boolean).join(' · ')}
                  onSelect={() => go(`/contracts/${c.id}`)}
                />
              ))}
            </Command.Group>
          )}
        </Command.List>

        <div
          style={{
            padding: '8px 14px',
            borderTop: 'var(--stroke-xs) solid var(--border-primary)',
            fontSize: 11,
            color: 'var(--text-tertiary)',
            display: 'flex',
            flexWrap: 'wrap',
            rowGap: 6,
            columnGap: 14,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span className="row" style={{ gap: 12 }}>
            <KbdHint keys={['↑', '↓']} desc="navigate" />
            <KbdHint keys={['↵']} desc="select" />
            <KbdHint keys={['esc']} desc="close" />
          </span>
          <span className="muted row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span><span className="mono">&gt;</span> actions</span>
            <span><span className="mono">@</span> counterparties</span>
            <span><span className="mono">#</span> tags</span>
            <span><span className="mono">/</span> groups</span>
          </span>
        </div>
      </Command>
    </div>
  );
}

// Shared row styling so every item looks like every other item.
function CmdItem({ icon: Icon, label, hint, badge, onSelect, value }) {
  // cmdk uses the value attribute as the item's identity — combining label +
  // hint keeps it unique enough for static entries; server-result rows pass
  // an explicit id-based value so two records sharing a display name can't
  // collide.
  const itemValue = value || [label, hint].filter(Boolean).join(' ');
  return (
    <Command.Item
      className="cmdk-row"
      value={itemValue}
      onSelect={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      {Icon && <Icon className="w-[14px] h-[14px]" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >{label}</div>
        {hint && (
          <div className="muted" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {hint}
          </div>
        )}
      </div>
      {badge && (
        <span className="row" style={{ gap: 4, alignItems: 'center', flexShrink: 0 }}>{badge}</span>
      )}
      <ArrowRight className="w-[11px] h-[11px]" style={{ color: 'var(--icon-tertiary)', opacity: 0.6 }} />
    </Command.Item>
  );
}

function KbdHint({ keys, desc }) {
  return (
    <span className="row" style={{ gap: 4, alignItems: 'center' }}>
      {keys.map((k) => (
        <kbd
          key={k}
          className="mono"
          style={{
            padding: '1px 5px',
            background: 'var(--surface-base)',
            border: '0.5px solid var(--border-primary)',
            borderRadius: 4,
            fontSize: 10,
            color: 'var(--text-secondary)',
          }}
        >{k}</kbd>
      ))}
      <span>{desc}</span>
    </span>
  );
}

// Simple case-insensitive substring match. `cmdk` does its own filtering on
// the Command.Item value when shouldFilter=true, but for scoped modes we
// want to pre-filter the data source before rendering so the cap-at-N
// kicks in on the visible subset, not on the first N of the entire list.
function filterItems(items, term, keyFn) {
  if (!term) return items;
  const t = term.toLowerCase();
  return items.filter((x) => (keyFn(x) || '').toLowerCase().includes(t));
}
